import React, { useEffect, useState } from 'react'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import StepUpPolicyCard from '@devnw/cyber-iam/iam/StepUpPolicyCard/StepUpPolicyCard'
import type { StepUpPolicy } from '@devnw/cyber-iam/iam/types'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { PamLoading, PamError } from '../components/PamStateComponents'
import { useAdminGenClient } from '../api/genClient'
import type { AdminGetStepUpPolicyResp } from '../../../gen/client/react/client'

// StepUpPolicy displays the current effective step-up assurance policy for the
// active tenant. In Epic 3 the policy is read-only (DefaultPrivilegedPolicy);
// full CRUD editing is deferred to Epic 4.
//
// SECURITY: Uses useAdminGenClient().adminGetStepUpPolicy(), injecting the
// Authorization: Bearer <token> header automatically via the generated client.
//
// OIDCPAM Gate-6 Phase 1 (pilot) — wire mapping: the generated client's
// decodeJSON() already snake_case -> camelCase converts the raw HTTP response
// body (gen/client/react/client.ts's fromWire()/convertKeys()), so
// AdminGetStepUpPolicyResp is ALREADY camelCase at this boundary — it is not
// the raw wire DTO. The only field that doesn't already match
// StepUpPolicyCard's `StepUpPolicy` prop name 1:1 is `maxAgeSecs` ->
// `maxAgeSeconds`; `requiredLevel` and `requirePhishingResistant` pass
// through unchanged. (The page's previous local `StepUpPolicyShape` used
// snake_case field names against this already-camelCase response and was
// therefore stale/dead code — this mapping fixes that as a side effect.)
function toStepUpPolicy(resp: AdminGetStepUpPolicyResp): StepUpPolicy {
  return {
    requiredLevel: resp.requiredLevel,
    maxAgeSeconds: resp.maxAgeSecs,
    requirePhishingResistant: resp.requirePhishingResistant,
  }
}

const StepUpPolicy = (): JSX.Element | null => {
  const { tenant: tenantId } = useActiveTenant()
  const client = useAdminGenClient()
  const [policy, setPolicy] = useState<StepUpPolicy | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    client
      .adminGetStepUpPolicy({ tenantId })
      .then((p) => {
        setPolicy(toStepUpPolicy(p))
        setLoading(false)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e))
        setLoading(false)
      })
  }, [client, tenantId])

  if (loading) return <PamLoading />
  if (error) return <PamError message={error} />
  if (!policy) return null

  return (
    <StepUpPolicyCard
      testID="stepup-policy"
      title="Step-up Authentication Policy"
      description={`Effective policy for tenant ${tenantId}. Full CRUD editing is available in Epic 4.`}
      policy={policy}
    />
  )
}

export default StepUpPolicy
